import {
  formatHistoryMonth,
  historyCompetenceIso,
  type StudentHistoryCandidate,
  type StudentHistoryImportStatus,
  type StudentNameMatch,
} from './studentWorkoutHistoryImport';

export type { StudentHistoryCandidate, StudentHistoryImportStatus, StudentNameMatch };

export interface FollowUpAssignment {
  studentId: number;
  previousCompetence: string;
  assignmentId: number;
  workoutCode: string | null;
  open?: boolean;
}

export interface FollowUpExistingAttempt {
  assignmentId: number;
  attemptNumber: number;
}

export interface FollowUpImportRow {
  id: string;
  studentName: string;
  periodYear: number;
  periodMonth: number;
  attemptNumber: number;
  rawValue: string;
  mentionedDateText: string | null;
  status: StudentHistoryImportStatus;
  reason: string | null;
  studentId: number | null;
  matchedStudentName: string | null;
  candidateStudents: StudentHistoryCandidate[];
  assignmentId: number | null;
  workoutCode: string | null;
}

export interface FollowUpMatchResult {
  studentsByName: Record<string, StudentNameMatch>;
  assignments: readonly FollowUpAssignment[];
  existingAttempts: readonly FollowUpExistingAttempt[];
}

const FOLLOW_UP_DOMAIN = 'FollowUpHistory';
const REASON_MONTH = 'Sem mês';
const REASON_STUDENT = 'Aluna não encontrada';
const REASON_STUDENTS = 'Mais de uma aluna com nome compatível';
const REASON_ASSIGNMENT = 'Sem treino do mês para vincular o FUP';
const REASON_ASSIGNMENTS = 'Mais de um treino neste mês';
const REASON_DUPLICATE = 'Já cadastrado';

const statusOrder: Record<StudentHistoryImportStatus, number> = {
  ready: 0,
  ambiguous: 1,
  duplicate: 2,
  blocked: 3,
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const readText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const readInteger = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number.NaN;
  return Number.isInteger(parsed) ? parsed : null;
};

const readMonth = (value: unknown): number | null => {
  const parsed = readInteger(value);
  if (parsed === null || parsed < 1 || parsed > 12) {
    return null;
  }
  return parsed;
};

const readYear = (value: unknown): number | null => {
  const parsed = readInteger(value);
  if (parsed === null || parsed < 1900 || parsed > 2100) {
    return null;
  }
  return parsed;
};

const compareRows = (left: FollowUpImportRow, right: FollowUpImportRow): number => (
  statusOrder[left.status] - statusOrder[right.status]
  || left.studentName.localeCompare(right.studentName, 'pt-BR')
  || left.periodYear - right.periodYear
  || left.periodMonth - right.periodMonth
  || left.attemptNumber - right.attemptNumber
);

const sortRows = (rows: readonly FollowUpImportRow[]): FollowUpImportRow[] => rows.slice().sort(compareRows);

interface ParsedCell {
  sourceRowNumber: number;
  sourceColumnIndex: number;
  studentName: string;
  periodYear: number | null;
  periodMonth: number | null;
  rawValue: string;
  mentionedDateText: string | null;
}

const buildRow = (
  cell: ParsedCell,
  attemptNumber: number,
  status: StudentHistoryImportStatus,
  reason: string | null,
): FollowUpImportRow => ({
  id: `${cell.sourceRowNumber}-${cell.sourceColumnIndex}-${attemptNumber}`,
  studentName: cell.studentName,
  periodYear: cell.periodYear ?? 0,
  periodMonth: cell.periodMonth ?? 0,
  attemptNumber,
  rawValue: cell.rawValue,
  mentionedDateText: cell.mentionedDateText,
  status,
  reason,
  studentId: null,
  matchedStudentName: null,
  candidateStudents: [],
  assignmentId: null,
  workoutCode: null,
});

export const formatFollowUpContact = (row: Pick<FollowUpImportRow, 'attemptNumber' | 'mentionedDateText'>): string => {
  const attempt = `Tentativa ${row.attemptNumber}`;
  return row.mentionedDateText ? `${attempt} · ${row.mentionedDateText}` : attempt;
};

export const formatFollowUpMonth = (row: Pick<FollowUpImportRow, 'periodYear' | 'periodMonth' | 'reason'>): string => {
  if (row.reason === REASON_MONTH || row.periodYear < 1900 || row.periodMonth < 1) {
    return 'Sem mês';
  }
  return formatHistoryMonth(row.periodYear, row.periodMonth);
};

export const parseStudentFollowUpImport = (payload: unknown): FollowUpImportRow[] => {
  if (!isRecord(payload) || !isRecord(payload.metadata) || payload.metadata.domain !== FOLLOW_UP_DOMAIN) {
    throw new Error('Envie um JSON de histórico de FUP (follow-up-history).');
  }
  if (!Array.isArray(payload.students)) {
    throw new Error('Envie um JSON de histórico de FUP (follow-up-history).');
  }

  const cells: ParsedCell[] = [];
  payload.students.forEach((student, index) => {
    if (!isRecord(student) || !Array.isArray(student.records)) {
      return;
    }
    const studentName = readText(student.studentName);
    if (studentName.length === 0) {
      return;
    }
    const sourceRowNumber = readInteger(student.sourceRowNumber) ?? index + 1;
    student.records.forEach((record) => {
      if (!isRecord(record)) {
        return;
      }
      const rawValue = readText(record.rawValue);
      if (rawValue.length === 0) {
        return;
      }
      const mentionedDateText = readText(record.mentionedDateText);
      cells.push({
        sourceRowNumber,
        sourceColumnIndex: readInteger(record.sourceColumnIndex) ?? 0,
        studentName,
        periodYear: readYear(record.periodYear),
        periodMonth: readMonth(record.periodMonth),
        rawValue,
        mentionedDateText: mentionedDateText.length > 0 ? mentionedDateText : null,
      });
    });
  });

  const rows: FollowUpImportRow[] = [];
  const groups = new Map<string, ParsedCell[]>();
  cells.forEach((cell) => {
    if (cell.periodYear === null || cell.periodMonth === null) {
      rows.push(buildRow(cell, 1, 'blocked', REASON_MONTH));
      return;
    }
    const key = `${cell.studentName}|${cell.periodYear}|${cell.periodMonth}`;
    const current = groups.get(key) ?? [];
    current.push(cell);
    groups.set(key, current);
  });

  groups.forEach((group) => {
    group
      .slice()
      .sort((left, right) => left.sourceColumnIndex - right.sourceColumnIndex)
      .forEach((cell, index) => {
        rows.push(buildRow(cell, index + 1, 'ready', null));
      });
  });

  return sortRows(rows);
};

const attemptKey = (assignmentId: number, attemptNumber: number): string => (
  `${assignmentId}|${attemptNumber}`
);

const assignmentsFor = (
  assignments: readonly FollowUpAssignment[],
  studentId: number,
  year: number,
  month: number,
): FollowUpAssignment[] => {
  const competence = historyCompetenceIso(year, month);
  const monthMatches = assignments.filter((assignment) => (
    assignment.studentId === studentId
    && !assignment.open
    && assignment.previousCompetence.slice(0, 10) === competence
  ));
  if (monthMatches.length > 0) {
    return monthMatches;
  }

  const openAssignments = assignments.filter((assignment) => (
    assignment.studentId === studentId && assignment.open
  ));
  const latestDeliveryMonth = assignments
    .filter((assignment) => assignment.studentId === studentId && !assignment.open)
    .map((assignment) => assignment.previousCompetence.slice(0, 10))
    .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))
    .sort()
    .at(-1) ?? '';
  if (openAssignments.length === 1 && (!latestDeliveryMonth || competence > latestDeliveryMonth)) {
    return openAssignments;
  }

  return [];
};

const withAssignment = (
  row: FollowUpImportRow,
  studentId: number,
  fullName: string,
  assignments: readonly FollowUpAssignment[],
  existing: Set<string>,
): FollowUpImportRow => {
  const matches = assignmentsFor(assignments, studentId, row.periodYear, row.periodMonth);
  if (matches.length === 0) {
    return {
      ...row,
      status: 'blocked',
      reason: REASON_ASSIGNMENT,
      studentId,
      matchedStudentName: fullName,
      assignmentId: null,
      workoutCode: null,
    };
  }
  if (matches.length > 1) {
    return {
      ...row,
      status: 'blocked',
      reason: REASON_ASSIGNMENTS,
      studentId,
      matchedStudentName: fullName,
      assignmentId: null,
      workoutCode: null,
    };
  }

  const assignment = matches[0];
  const duplicate = existing.has(attemptKey(assignment.assignmentId, row.attemptNumber));
  return {
    ...row,
    status: duplicate ? 'duplicate' : 'ready',
    reason: duplicate ? REASON_DUPLICATE : null,
    studentId,
    matchedStudentName: fullName,
    assignmentId: assignment.assignmentId,
    workoutCode: assignment.workoutCode,
  };
};

export const applyFollowUpMatches = (
  rows: readonly FollowUpImportRow[],
  result: FollowUpMatchResult,
): FollowUpImportRow[] => {
  const existing = new Set(result.existingAttempts.map((item) => attemptKey(item.assignmentId, item.attemptNumber)));

  return sortRows(rows.map((row) => {
    if (row.status === 'blocked') {
      return row;
    }

    const student = result.studentsByName[row.studentName] ?? { status: 'none' as const };
    if (student.status === 'none') {
      return { ...row, status: 'blocked', reason: REASON_STUDENT };
    }
    if (student.status === 'many') {
      return {
        ...row,
        status: 'ambiguous',
        reason: REASON_STUDENTS,
        candidateStudents: student.candidates,
      };
    }

    return withAssignment(row, student.studentId, student.fullName, result.assignments, existing);
  }));
};

export const chooseFollowUpMatch = (
  rows: readonly FollowUpImportRow[],
  studentName: string,
  studentId: number | null,
  result: FollowUpMatchResult,
): FollowUpImportRow[] => {
  const existing = new Set(result.existingAttempts.map((item) => attemptKey(item.assignmentId, item.attemptNumber)));

  return sortRows(rows.map((row) => {
    if (row.studentName !== studentName || row.status === 'blocked' || row.candidateStudents.length === 0) {
      return row;
    }

    if (studentId === null) {
      return {
        ...row,
        status: 'ambiguous',
        reason: REASON_STUDENTS,
        studentId: null,
        matchedStudentName: null,
        assignmentId: null,
        workoutCode: null,
      };
    }

    const chosen = row.candidateStudents.find((candidate) => candidate.id === studentId);
    if (!chosen) {
      return row;
    }

    return {
      ...withAssignment(row, chosen.id, chosen.fullName, result.assignments, existing),
      candidateStudents: row.candidateStudents,
    };
  }));
};
